import React from "react";
import styled from "styled-components";
import { eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek } from "date-fns";
import CustomIcon from "../image/CustomIcon";
import { C2Text, C4Text } from "../text/CustomText";
import { Divider } from "../CommonBaseComponents";
import { Theme } from "../../Theme";

const CalendarContainer = styled.div`
    width: 100%;
    border-left: 1px solid #e6e6e9;
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
`;

const CalendarHeader = styled(Grid)`
    border-bottom: 1px solid #e6e6e9;
    background-color: #f6f6f6;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
`;

const DayNameCell = styled.div`
    padding: 12px 8px;
    text-align: start;
    border-top-left-radius: ${(props) => (props.isFirstItem ? "16px" : "0")};
    border-top-right-radius: ${(props) => (props.isLastItem ? "16px" : "0")};
    border-right: 1px solid #e6e6e9;
    border-top: 1px solid #e6e6e9;

    font-weight: 600;
    font-size: 12px;
    color: #555;
`;

const CalendarGrid = styled(Grid)``;

const DayCellContainer = styled.div`
    border-right: 1px solid #e6e6e9;
    border-bottom: 1px solid #e6e6e9;

    padding: 8px;
    box-sizing: border-box;
    min-height: 140px; /* 셀의 최소 높이 */
    min-width: 125px;
    position: relative;

    border-bottom-left-radius: ${(props) => (props.$isBottomLeft ? "16px" : "0")};
    border-bottom-right-radius: ${(props) => (props.$isBottomRight ? "16px" : "0")};
`;

const DayContentWrapper = styled.div`
    opacity: ${(props) => (props.$isOtherMonth ? 0.4 : 1)};
`;

const DayDataList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px; /* 아이템 간의 간격 */
`;

const DataItemContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px; /* 아이콘과 텍스트 사이 간격 */
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.9em;

    background-color: ${(props) => props.$bgColor || Theme.color.zinc50};

    span:first-child {
        font-size: 0.8em;
    }

    span:last-child {
        font-weight: 500;
    }
`;

function DataItem({ icon, label, color }) {
    return (
        <DataItemContainer $bgColor={color}>
            <CustomIcon iconName={icon} size={"16px"} />
            <C4Text fontWeight={"500"}>{label.toLocaleString()}</C4Text>
        </DataItemContainer>
    );
}

function DayCell({ day, currentMonth, data, isBottomLeft, isBottomRight }) {
    // 이번 달에 속하지 않는 날짜인지 확인 (e.g., 10/30, 12/01)
    const isOtherMonth = !isSameMonth(day, currentMonth);
    // 날짜 포맷 (e.g., '1', '2', ... '30')
    const formattedDay = format(day, "d");

    return (
        <DayCellContainer $isBottomLeft={isBottomLeft} $isBottomRight={isBottomRight}>
            <DayContentWrapper $isOtherMonth={isOtherMonth}>
                <C2Text>{formattedDay}</C2Text>
                <Divider thickness={"0"} marginTop={"8px"} />
                {/* 해당 날짜에 데이터가 있는 경우에만 리스트를 렌더링 */}
                {data && (
                    <DayDataList>
                        {data.store && (
                            <DataItem icon="cashes_black" label={data.store} color={Theme.color.neutral100} />
                        )}
                        {data.washer && <DataItem icon="washer" label={data.washer} color={Theme.color.cyan600_007} />}
                        {data.dryer && <DataItem icon="dryer" label={data.dryer} color={Theme.color.red500_01} />}
                        {data.combo && <DataItem icon="combo" label={data.combo} color={Theme.color.indigo800_007} />}
                    </DayDataList>
                )}
            </DayContentWrapper>
        </DayCellContainer>
    );
}

function CustomCalendarGrid({ currentMonth, data }) {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
    const totalDays = days.length; // 1. 그리드의 총 칸 수 (e.g., 42)

    const dayNames = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

    return (
        <CalendarContainer>
            <CalendarHeader>
                {dayNames.map((dayName, index) => (
                    <DayNameCell key={dayName} isFirstItem={index === 0} isLastItem={index === 6}>
                        {dayName}
                    </DayNameCell>
                ))}
            </CalendarHeader>

            <CalendarGrid>
                {days.map((day, index) => {
                    // 'yyyy-MM-dd' 형식의 키로 FAKE_DATA에서 데이터 조회
                    const dataKey = format(day, "yyyy-MM-dd");
                    const dayData = data[dataKey];

                    const isBottomLeft = index === totalDays - 7;
                    const isBottomRight = index === totalDays - 1;

                    return (
                        <DayCell
                            key={day.toString()}
                            day={day}
                            currentMonth={currentMonth}
                            data={dayData}
                            isBottomLeft={isBottomLeft}
                            isBottomRight={isBottomRight}
                        />
                    );
                })}
            </CalendarGrid>
        </CalendarContainer>
    );
}

export default CustomCalendarGrid;
